import { useCallback, useMemo } from 'react';

import { useSelectionAnnouncer } from '../useSelectionAnnouncer/useSelectionAnnouncer';

import { type ChoiceCardsProps, type ChoiceCardsRow } from './ChoiceCards.types';

export type UseChoiceCardsLogicInput = Pick<
  ChoiceCardsProps,
  'items' | 'onValueChange' | 'isDisabled' | 'isReadOnly'
>;

export interface UseChoiceCardsLogic {
  rows: readonly ChoiceCardsRow[];
  /** A caller that declared no items — worth showing rather than rendering nothing. */
  isEmpty: boolean;
  select: (value: string) => void;
  announcement: string;
}

/**
 * A named set of mutually exclusive cards over ONE string value — the
 * segmented sibling `ToggleGroup` (many independent booleans) and `Select`
 * (options collapsed behind a trigger) both fail: three choices whose hints
 * are the thing being compared want to sit side by side, visible at once.
 *
 * Takes no `value`: unlike `ToggleGroup`, which computes each row's own
 * checked state from a map it owns, a single choice is exactly what Radix's
 * `RadioGroup` already tracks — the component hands `value` straight to the
 * `Root` and each `Item` compares itself against it natively.
 */
export function useChoiceCardsLogic({
  items,
  onValueChange,
  isDisabled = false,
  isReadOnly = false,
}: UseChoiceCardsLogicInput): UseChoiceCardsLogic {
  const { announcement, announceReadOnlyRefusal } = useSelectionAnnouncer();

  const rows = useMemo<readonly ChoiceCardsRow[]>(
    () =>
      items.map((item) => ({
        value: item.value,
        label: item.label,
        ...(item.description === undefined ? {} : { description: item.description }),
        isDisabled: isDisabled || item.isDisabled === true,
      })),
    [isDisabled, items],
  );

  const select = useCallback(
    (next: string) => {
      if (isReadOnly) {
        announceReadOnlyRefusal();
        return;
      }

      onValueChange(next);
    },
    [announceReadOnlyRefusal, isReadOnly, onValueChange],
  );

  return { rows, isEmpty: items.length === 0, select, announcement };
}
